/**
 * Two flags because "a create is in flight" and "this latch already settled a
 * session" are different facts; `projectId` scopes both.
 */
export interface InsightSessionLatch {
  isInFlight: boolean;
  hasSucceeded: boolean;
  projectId: string | null;
}

export function syncLatchToProject(
  latch: InsightSessionLatch,
  projectId: string
): void {
  if (latch.projectId === projectId) {
    return;
  }

  latch.projectId = projectId;
  latch.isInFlight = false;
  latch.hasSucceeded = false;
}

export type InsightSessionAction =
  | { readonly type: 'reuse'; readonly insightId: string }
  | { readonly type: 'create' };

/**
 * Three states, not `string | null`: "still resolving" and "could not be
 * resolved" both read as "no insight id yet", but one renders a loading state
 * and the other must render the error surface. Collapsing them is what would
 * leave a responder watching a spinner forever after a failed create.
 *
 * `isResumed` distinguishes an id read back from storage from one just minted:
 * only the first has a conversation behind it worth reading, and only the first
 * may move the responder off question one.
 */
export type InsightSessionState =
  | { readonly status: 'resolving' }
  | {
      readonly status: 'ready';
      readonly insightId: string;
      readonly isResumed: boolean;
      /**
       * The backend's own cursor on a freshly created session; `null` on a
       * resumed one, whose starting step comes from its loaded transcript
       * instead.
       */
      readonly firstQuestionId: string | null;
    }
  | { readonly status: 'failed' };

export const RESOLVING_INSIGHT_SESSION: InsightSessionState = {
  status: 'resolving',
};

export function resolveInsightSessionAction(
  storedInsightId: string | null
): InsightSessionAction {
  return storedInsightId === null
    ? { type: 'create' }
    : { type: 'reuse', insightId: storedInsightId };
}

/**
 * One session per page load. The store is memory-only on purpose: a hard
 * refresh must start a new session, so nothing may outlive the document. What
 * it still has to survive is a remount inside one load — the gate remounts on
 * the screener key, and a second `createInsight` there would mint a second
 * insight for one respondent.
 */
const insightIdByProject = new Map<string, string>();

export function readStoredInsightId(projectId: string): string | null {
  return insightIdByProject.get(projectId) ?? null;
}

export function writeStoredInsightId(
  projectId: string,
  insightId: string
): void {
  insightIdByProject.set(projectId, insightId);
}

export function clearStoredInsightId(projectId: string): void {
  insightIdByProject.delete(projectId);
}

export function resetInsightSessionStoreForTests(): void {
  insightIdByProject.clear();
}

export const INSIGHT_CLEAR_REASONS = {
  Completed: 'completed',
  Invalidated: 'invalidated',
} as const;

export type InsightClearReason =
  (typeof INSIGHT_CLEAR_REASONS)[keyof typeof INSIGHT_CLEAR_REASONS];

export interface ResponderInsightStore {
  read: (projectId: string) => string | null;
  write: (projectId: string, insightId: string) => void;
  clear: (projectId: string, reason: InsightClearReason) => void;
}

export const IN_MEMORY_INSIGHT_STORE: ResponderInsightStore = {
  read: readStoredInsightId,
  write: writeStoredInsightId,
  clear: clearStoredInsightId,
};

/**
 * Calls `createInsight` exactly once per latch, guarding both React's
 * double-invoke of an effect and a same-tick double call issued before the
 * first `await` yields — the same shape `confirmCompletionOnce` guards for the
 * opposite end of a session. A second call after the first has resolved is a
 * silent no-op rather than a second insight, which is what makes a remount
 * safe once the id is in the store.
 */
export interface CreatedInsight {
  readonly insightId: string;
  readonly firstQuestionId: string | null;
}

export async function createInsightOnce(
  latch: InsightSessionLatch,
  createInsight: () => Promise<CreatedInsight>,
  onCreated: (created: CreatedInsight) => void
): Promise<void> {
  if (latch.hasSucceeded || latch.isInFlight) {
    return;
  }

  latch.isInFlight = true;

  try {
    const result = await createInsight();

    latch.hasSucceeded = true;
    onCreated(result);
  } finally {
    latch.isInFlight = false;
  }
}

/** A rejected create reports `failed` and rethrows. */
export async function resolveInsightSession(
  latch: InsightSessionLatch,
  projectId: string,
  createInsight: () => Promise<CreatedInsight>,
  onResolved: (state: InsightSessionState) => void,
  store: ResponderInsightStore = IN_MEMORY_INSIGHT_STORE
): Promise<void> {
  syncLatchToProject(latch, projectId);

  if (latch.hasSucceeded) {
    return;
  }

  const action = resolveInsightSessionAction(store.read(projectId));

  if (action.type === 'reuse') {
    latch.hasSucceeded = true;
    onResolved({
      status: 'ready',
      insightId: action.insightId,
      isResumed: true,
      firstQuestionId: null,
    });

    return;
  }

  try {
    await createInsightOnce(latch, createInsight, (created) => {
      store.write(projectId, created.insightId);
      onResolved({
        status: 'ready',
        insightId: created.insightId,
        isResumed: false,
        firstQuestionId: created.firstQuestionId,
      });
    });
  } catch (error) {
    onResolved({ status: 'failed' });
    throw error;
  }
}
